import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DataTableDirective } from 'angular-datatables';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { RegistruService } from './registru.service';
import Swal from 'sweetalert2';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { AppFilesService } from 'src/app/services/app-files.service';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { MatCalendar } from '@angular/material/datepicker';
import { RegistruCategoryCount, RegistruStatusCard, RegistruSummary } from './registru.interface';

@Component({
    selector: 'app-registru',
    templateUrl: './registru.component.html',
    styleUrls: ['./registru.component.scss']
})
export class RegistruComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    @ViewChild(PopoverDirective) popover!: PopoverDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;

    // Popover dates

    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    tomorrow: Date = new Date();
    selectedStartDate: Date = new Date(2025, 0, 1);
    private _selectedEndDate: Date = new Date();
    selectedFilteringButton: string = 'current_year';

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);
        this.selectedStartDate = date;
        this.updateCalendarView(this.startCalendar, date);

        this.selectedEndDate = new Date(this.today);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);

        this.rerenderDT();
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedEndDate = yearsBack === 0 ? this.today : new Date(year, 11, 31);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);
        this.rerenderDT();
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date

    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    formatDate(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    // Date popover filtering

    dateFilteringOptions: { id: number; value: string }[] = [
        { id: 1, value: 'Adăugare' },
        { id: 2, value: 'Înregistrare' },
    ];

    dateFilterOption: number = 1;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    serii!: Generic[];

    private destroy$ = new Subject<void>();

    filteredSerii: any[] = [];
    seriiControl = new FormControl();

    categorii: Generic[] = [];

    activityStatusOptions: { id: string; name: string }[] = [
        { id: 'in_lucru', name: 'În lucru' },
        { id: 'finalizat', name: 'Finalizat' },
        { id: 'nepreluat', name: 'Nepreluat' },
        { id: 'invalidat', name: 'Invalidat' },
    ];

    // Widgeturi contorizare documente (din răspunsul list-dt)
    categoryCounts: RegistruCategoryCount[] = [];
    totalCategoryCount = 0;
    summary: RegistruSummary | null = null;
    /** Intrare, Intern, Ieșire, Arhivă - afișate mereu, în această ordine. */
    private readonly coreCategoryIds = [1, 3, 2, 4];

    get totalDocuments(): number {
        return this.summary?.total_documents ?? this.totalCategoryCount;
    }

    get statusCards(): RegistruStatusCard[] {
        const s = this.summary;
        if (!s) {
            return [];
        }

        const cards: RegistruStatusCard[] = [
            { id: 'in_lucru', label: 'În lucru', value: s.in_lucru, icon: 'autorenew', cssClass: 'kpi-in-lucru' },
            { id: 'finalizat', label: 'Finalizat', value: s.finalizat, icon: 'check_circle', cssClass: 'kpi-finalizat' },
            { id: 'nepreluat', label: 'Nepreluat', value: s.nepreluat, icon: 'hourglass_empty', cssClass: 'kpi-nepreluat' },
            { id: 'invalidat', label: 'Invalidat', value: s.invalidat, icon: 'cancel', cssClass: 'kpi-invalidat' },
            { id: 'other', label: 'Altele', value: s.other, icon: 'help_outline', cssClass: 'kpi-other' },
        ];

        return cards;
    }

    searchParamsForm!: FormGroup;

    initialFormValues = {
        numar_registru: null,
        id_serie: -1,
        id_categorii: [],
        activity_status: [],
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            numar_registru: new FormControl(null),
            id_serie: new FormControl(-1),
            id_categorii: new FormControl<number[]>([]),
            activity_status: new FormControl<string[]>([]),
        });
    }

    resetForm() {
        // Seria este un filtru de toolbar, în afara cardului - nu se resetează împreună cu restul.
        const currentSerie = this.searchParamsForm.get('id_serie')?.value;

        this.searchParamsForm.reset({ ...this.initialFormValues, id_serie: currentSerie });

        this.rerenderDT();
        this.showCard = true;
    }

    get serieControl(): FormControl {
        return this.searchParamsForm.get('id_serie') as FormControl;
    }

    get selectedCategorii(): Generic[] {
        const ids: number[] = this.searchParamsForm?.get('id_categorii')?.value ?? [];
        return this.categorii.filter((cat) => ids.includes(cat.id));
    }

    get selectedActivityStatus(): { id: string; name: string }[] {
        const ids: string[] = this.searchParamsForm?.get('activity_status')?.value ?? [];
        return this.activityStatusOptions.filter((st) => ids.includes(st.id));
    }

    get hasSelectedFilters(): boolean {
        return this.selectedCategorii.length > 0 || this.selectedActivityStatus.length > 0;
    }

    removeCategorie(id: number): void {
        const control = this.searchParamsForm.get('id_categorii');
        const values: number[] = control?.value ?? [];
        control?.setValue(values.filter((value) => value !== id));
    }

    removeActivityStatus(id: string): void {
        const control = this.searchParamsForm.get('activity_status');
        const values: string[] = control?.value ?? [];
        control?.setValue(values.filter((value) => value !== id));
    }

    clearSelectedFilters(): void {
        this.searchParamsForm.get('id_categorii')?.setValue([]);
        this.searchParamsForm.get('activity_status')?.setValue([]);
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    dtLink = environment.interop.basePath + environment.interop.indoc.registru.getRegistreDT;

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private registruService: RegistruService,
        public permissionsService: PermissionsService,
        private commonService: CommonService,
        private appFilesService: AppFilesService,
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
        this.renderFieldsSearch();
        this.loadSerii();
        this.loadCategorii();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderFieldsSearch() {
        this.seriiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredSerii = this.serii;
                    return of(this.serii);
                }
                return this.filterSerii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredSerii = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadSerii() {
        this.isFilterLoading = true;
        this.commonService.getActiveSwitch()
            .pipe(
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.serii = results;
                    this.filteredSerii = [...this.serii];
                },
                error: (error) => {
                    console.error('Error in get serii: ', error);
                }
            })
    }

    filterSerii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.serii.filter(serie =>
            this.normalizeString(serie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    loadCategorii() {
        this.isFilterLoading = true;
        this.commonService.getTipuriAccesActive()
            .pipe(
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results: Generic[]) => {
                    this.categorii = results;
                },
                error: (error) => {
                    console.error('Error in get categorii: ', error);
                    this.categorii = [];
                }
            })
    }

    categoryIcon(idCategorie: number): string {
        switch (idCategorie) {
            case 1: return 'call_received';
            case 2: return 'call_made';
            case 3: return 'sync_alt';
            case 4: return 'inventory_2';
            default: return 'folder';
        }
    }

    categoryClass(idCategorie: number): string {
        switch (idCategorie) {
            case 1: return 'kpi-intrare';
            case 2: return 'kpi-iesire';
            case 3: return 'kpi-intern';
            case 4: return 'kpi-arhiva';
            default: return 'kpi-custom';
        }
    }

    private buildSummary(raw: any): RegistruSummary | null {
        if (!raw) {
            return null;
        }

        return {
            total_documents: Number(raw.total_documents ?? 0),
            in_lucru: Number(raw.in_lucru ?? 0),
            finalizat: Number(raw.finalizat ?? 0),
            nepreluat: Number(raw.nepreluat ?? 0),
            invalidat: Number(raw.invalidat ?? 0),
            other: Number(raw.other ?? 0),
        };
    }

    private buildCategoryCounts(raw: any): RegistruCategoryCount[] {
        const list: RegistruCategoryCount[] = (Array.isArray(raw) ? raw : []).map((item: any) => ({
            id_categorie: Number(item.id_categorie),
            categorie: String(item.categorie ?? ''),
            total: Number(item.total ?? 0),
        }));

        this.totalCategoryCount = list.reduce((acc, item) => acc + item.total, 0);

        // Categoriile de bază se afișează mereu; cele personalizate doar dacă au documente.
        return list
            .filter((item) => this.coreCategoryIds.includes(item.id_categorie) || item.total > 0)
            .sort((a, b) => {
                const indexA = this.coreCategoryIds.indexOf(a.id_categorie);
                const indexB = this.coreCategoryIds.indexOf(b.id_categorie);

                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;

                return b.total - a.total;
            });
    }

    onSerieSelectClosed() {
        const selectedValue = this.seriiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredSerii.some(serie =>
                serie.id === this.searchParamsForm.get('id_serie')?.value
            );

            if (!isSelectedInFilteredList) {
                this.seriiControl.setValue(null);
                this.filteredSerii = this.serii;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    renderDT() {
        const self = this;
        this.isLoading = true;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            order: [[0, 'desc']],

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                const formValues = { ...this.searchParamsForm.value };
                formValues.id_categorii = formValues.id_categorii?.length ? formValues.id_categorii : null;
                formValues.activity_status = formValues.activity_status?.length ? formValues.activity_status : null;

                Object.assign(dataTablesParameters, formValues);
                dataTablesParameters.tip_date = this.dateFilterOption;
                dataTablesParameters.date_from = this.formatDate(
                    this.selectedStartDate
                );
                dataTablesParameters.date_to = this.formatDate(this.selectedEndDate);

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        this.categoryCounts = this.buildCategoryCounts(resp.data?.category_counts);
                        this.summary = this.buildSummary(resp.data?.summary);
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID(Unic)',
                    data: 'id_document',
                    render: (data: any, type: any, row: any, met: any) => {
                        return `
                                <div class="d-flex align-items-center gap-1">
                                   <button class="action-btn download redirect" style="font-size: 11px; color: #fff" title="Deschide document">
                                        <i class="fa fa-external-link"></i> ${row.id_doc}
                                    </button> 
                                </div>
                            `;
                    },
                },
                {
                    title: 'Registru',
                    data: 'registratura_info',
                },
                {
                    title: 'Categorie',
                    data: 'categorie',
                    sortable: false,
                },
                {
                    title: 'Serie',
                    data: 'name_seq',
                    sortable: false,
                },
                {
                    title: 'Tip Document',
                    data: 'document_type',
                    sortable: false,
                },
                {
                    title: 'Compartiment',
                    data: 'compartiment_name',
                    sortable: false,
                },
                {
                    title: 'Întocmitor',
                    data: 'intocmitor',
                    sortable: false,
                },
                {
                    title: 'Emitent',
                    data: 'emitent',
                    sortable: false,
                },
                {
                    title: 'Destinatar',
                    data: 'destinatar',
                    sortable: false,
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const firstTd = (row as HTMLElement).querySelector('td:first-child');
                const redirectButton = firstTd?.querySelector('.redirect') as HTMLElement;

                if (redirectButton) {
                    redirectButton.addEventListener('click', () => {
                        const { id_document } = data;
                        // Redirect to document url
                        const url = `#/documente?id=${id_document}`;
                        window.open(url, '_blank');
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });

        this.showCard = false;
        this.closePopover();
    }

    download() {
        const id_serie = this.searchParamsForm.get('id_serie')?.value;
        const date_from = this.formatDate(this.selectedStartDate);
        const date_to = this.formatDate(this.selectedEndDate);

        const params = new URLSearchParams();
        params.set('serie', id_serie);
        params.set('date_from', date_from);
        params.set('date_end', date_to);
        params.set('tip_date', String(this.dateFilterOption));

        const numarRegistru = this.searchParamsForm.get('numar_registru')?.value;
        if (numarRegistru !== null && numarRegistru !== '') {
            params.set('numar_registru', numarRegistru);
        }

        const idCategorii: number[] = this.searchParamsForm.get('id_categorii')?.value ?? [];
        idCategorii.forEach((id) => params.append('id_categorii[]', String(id)));

        const activityStatus: string[] = this.searchParamsForm.get('activity_status')?.value ?? [];
        activityStatus.forEach((status) => params.append('activity_status[]', status));

        let fullPath = this.registruService.downloadTabel + `?${params.toString()}`;
        // let fullPath = this.registruService.downloadTabel + `?serie=${id_serie}&date_from=${date_from}&date_end=${date_to}`;
        this.isLoading = true;
        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Download tabel error: ', err);
                this.isLoading = false;
            })
    }
}
